import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { AppShellService } from '@neutrino/core/app-shell';
import { Observable, merge } from 'rxjs';
import { delay, filter, map, share, skip, switchMap, take } from 'rxjs/operators';
import logger from 'loglevel';

import { clientRoutes } from '@flb/common/client-routes';
import { notNullish } from '@flb/common/util';

import { ClientStateService } from './common/client-state.service.js';
import { NavSectionService } from './nav-section.service.js';
import { AccessLevel } from '@flb/common/access-level';

const routePath = (r: (typeof clientRoutes)[keyof typeof clientRoutes]) =>
    typeof r === 'string' ? r : r.path;
const preloadRoutes = {
    // if navigation is shown, pull the adminLogin module
    navigationShown: [clientRoutes.login].map(routePath),
    // adminAll is added below
    adminAll: [] as string[],
    // if the user expands the admin section in the navbar
    admin: [clientRoutes.admin, clientRoutes.login].map(routePath),
    // if the user expands the diagnostics section in the navbar
    diagnostics: [clientRoutes.login, clientRoutes.diagnostics].map(routePath),
    // if the user expands the commentator section of the navbar, or has a commentator password stored
    readonly: [
        clientRoutes.analysis,
        clientRoutes.info,
        clientRoutes.commentator,
        clientRoutes.login
    ].map(routePath)
};

// if the admin password is saved in the session storage, pull all the admin modules
preloadRoutes.adminAll.push(
    ...preloadRoutes.readonly,
    ...preloadRoutes.admin,
    ...preloadRoutes.diagnostics
);
// delay auto-preload due to passwords by 2.5s
// it saves 100ms when loading from localhost
const AUTO_PRELOAD_DELAY = 2500;

/**
 * Preload strategy to prevent having to wait for modules to load when you click on the navbar.
 * The idea is that if the user goes near the navbar we selectively start loading modules.
 */
@Injectable()
export class ClientStatePreloadingStrategy implements PreloadingStrategy {
    private conditions;
    constructor(
        private appShell: AppShellService,
        private clientState: ClientStateService,
        private navSection: NavSectionService
    ) {
        this.conditions = merge(
            this.appShell.navigationCollapsed.pipe(
                // skip the first event, it's probably the original state, we're only interested
                // in user interaction.
                skip(1),
                filter(collapsed => !collapsed),
                map(() => preloadRoutes.navigationShown),
                map(routes => ({ routes, reason: 'navigation shown' }))
            ),
            this.clientState.authToken.pipe(
                delay(AUTO_PRELOAD_DELAY),
                filter(t => t?.accessLevel === AccessLevel.Admin),
                map(() => preloadRoutes.adminAll),
                map(routes => ({ routes, reason: 'admin access token' }))
            ),
            this.clientState.authToken.pipe(
                delay(AUTO_PRELOAD_DELAY),
                filter(t => t?.accessLevel === AccessLevel.Readonly),
                map(() => preloadRoutes.readonly),
                map(routes => ({ routes, reason: 'readonly access token' }))
            ),
            this.navSection.clickedSection.pipe(
                filter(notNullish),
                map(n => ({
                    routes:
                        preloadRoutes[n.label?.toLowerCase() as keyof typeof preloadRoutes] || [],
                    label: n.label
                })),
                map(({ routes, label }) => ({ routes, reason: `section clicked: ${label}` }))
            )
        ).pipe(share({ resetOnRefCountZero: true }));
    }
    preload(route: Route, load: () => Observable<any>): Observable<any> {
        return this.conditions.pipe(
            filter(({ routes }) => !!route.path && routes.includes(route.path)),
            take(1),
            switchMap(({ reason }) => {
                logger.debug(`preload ${route.path} because ${reason}`);
                return load();
            })
        );
    }
}
